import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getAuthMode,
  isDevIamLoginMode,
  isDevKeycloakPasswordMode,
  isDevKeycloakRedirectMode,
  requiresMarchAgentLogin,
} from './authMode';
import {
  decodeJwtPayload,
  profileDisplayName,
  profileRoles,
  type DecodedJwtProfile,
} from './jwtPayload';
import { subscribeAuthSyncEvents } from './authSync';
import { purgeAllThreadStorage } from '../services/threadService';
import { purgePersistedTrafficEvents } from '../contexts/trafficEventStorage';
import {
  completeKeycloakCallbackIfPresent,
  isKeycloakConfigReady,
  startKeycloakLogin,
} from './keycloakClient';
import {
  isPasswordGrantConfigReady,
  loginWithKeycloakPassword,
} from './keycloakPasswordClient';
import { LoginPage } from './LoginPage';
import {
  capturePendingSmartLaunchFromLocation,
  completePendingSmartLaunchRestoration,
  getPendingSmartLaunch,
  hasPendingSmartLaunch,
  validatePendingSmartLaunch,
} from './pendingSmartLaunch';
import {
  getDevAccessToken,
  getRememberedUsername,
  getStaySignedIn,
  getStoredAccessToken,
  getStoredClaims,
  hasDevAccessToken,
  reloadTokenStateFromStorage,
  setStaySignedIn,
} from './tokenStore';
import {
  clearIamAccessProfile,
  fetchIamAccessProfile,
  getCachedIamAccessProfile,
  isIamAccessCacheExpired,
  refreshIamAccessProfile,
} from './iamAccess';
import { subscribeIamProfileSyncEvents } from './iamProfileSync';
import { demoFallbackRoles } from './demoRoles';
import { closePatientConnection } from '../api/smartApi';
import { reconcileClinicianScope } from '../api/smartStorageScope';
import { getPatientContextCoordinator } from '../api/patientContextCoordinator';
import {
  ensureAuthenticated,
  logout as logoutSession,
  onSessionExpired,
  SESSION_EXPIRED_MESSAGE,
} from './tokenLifecycle';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

type AuthContextValue = {
  status: AuthStatus;
  profile: DecodedJwtProfile | null;
  displayName: string;
  roles: string[];
  loginRedirect: (options?: { staySignedIn?: boolean }) => Promise<void>;
  loginWithPassword: (
    username: string,
    password: string,
    options?: { rememberUsername?: boolean; staySignedIn?: boolean },
  ) => Promise<void>;
  logout: () => void;
  isDevIam: boolean;
  /** @deprecated use isDevIam */
  isLocalIam: boolean;
  /** @deprecated use isDevIam */
  isLocalKeycloak: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return {
      status: 'authenticated',
      profile: null,
      displayName: '',
      roles: [],
      loginRedirect: async () => undefined,
      loginWithPassword: async () => undefined,
      logout: () => undefined,
      isDevIam: false,
      isLocalIam: false,
      isLocalKeycloak: false,
    };
  }
  return ctx;
}

function LoadingShell() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <p className="text-sm text-text-tertiary">Preparing development authentication…</p>
    </div>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const devIamMode = isDevIamLoginMode();
  const requiresLogin = requiresMarchAgentLogin();
  const authMode = getAuthMode();
  const passwordMode = isDevKeycloakPasswordMode();
  const redirectMode = isDevKeycloakRedirectMode();
  const [status, setStatus] = useState<AuthStatus>(() => {
    if (!requiresLogin) return 'authenticated';
    return hasDevAccessToken() ? 'authenticated' : 'anonymous';
  });
  const [oauthBooting, setOauthBooting] = useState(redirectMode);
  const [sessionBooting, setSessionBooting] = useState(passwordMode);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [smartLaunchError, setSmartLaunchError] = useState<string | null>(null);
  const [profile, setProfile] = useState<DecodedJwtProfile | null>(null);
  const [, setAccessProfileBooting] = useState(
    () => requiresLogin && !getCachedIamAccessProfile(),
  );

  const loadAccessProfile = useCallback(async (roles: string[]) => {
    if (getCachedIamAccessProfile() && !isIamAccessCacheExpired()) {
      setAccessProfileBooting(false);
      return;
    }
    setAccessProfileBooting(true);
    try {
      await fetchIamAccessProfile({
        rolesFallback: roles,
        force: !getCachedIamAccessProfile(),
      });
    } finally {
      setAccessProfileBooting(false);
    }
  }, []);

  const refreshProfile = useCallback(() => {
    reloadTokenStateFromStorage();
    const stored = getStoredClaims();
    const token = getDevAccessToken() || getStoredAccessToken();
    setProfile(stored ?? (token ? decodeJwtPayload(token) : null));
    setStatus(hasDevAccessToken() ? 'authenticated' : 'anonymous');
  }, []);

  const restoreSmartContextIfAuthenticated = useCallback(() => {
    if (!hasDevAccessToken()) return;
    completePendingSmartLaunchRestoration();
  }, []);

  useEffect(() => {
    reloadTokenStateFromStorage();
  }, []);

  useEffect(() => {
    return onSessionExpired(() => {
      clearIamAccessProfile();
      setProfile(null);
      setStatus('anonymous');
      setLoginError(SESSION_EXPIRED_MESSAGE);
    });
  }, []);

  useEffect(() => {
    return subscribeAuthSyncEvents((eventType) => {
      if (eventType === 'auth:token-updated') {
        reloadTokenStateFromStorage();
        refreshProfile();
        const stored = getStoredClaims();
        const token = getDevAccessToken() || getStoredAccessToken();
        const decoded = stored ?? (token ? decodeJwtPayload(token) : null);
        void loadAccessProfile(profileRoles(decoded));
        return;
      }
      if (eventType === 'auth:logout' || eventType === 'auth:session-expired') {
        clearIamAccessProfile();
        setProfile(null);
        setStatus('anonymous');
        if (eventType === 'auth:session-expired') {
          setLoginError(SESSION_EXPIRED_MESSAGE);
        }
      }
    });
  }, [refreshProfile, loadAccessProfile]);

  useEffect(() => {
    return subscribeIamProfileSyncEvents((event) => {
      if (event.type === 'auth-logout' || event.type === 'auth-token-refresh-failed') {
        clearIamAccessProfile(event.type);
        setProfile(null);
        setStatus('anonymous');
        if (event.type === 'auth-token-refresh-failed') {
          setLoginError(SESSION_EXPIRED_MESSAGE);
        }
      }
    });
  }, []);

  useEffect(() => {
    if (!requiresLogin || status !== 'authenticated') return undefined;
    const onFocus = () => {
      if (isIamAccessCacheExpired()) {
        void refreshIamAccessProfile({ rolesFallback: profileRoles(profile) });
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [requiresLogin, status, profile]);

  useEffect(() => {
    if (!requiresLogin) {
      // IAM-disabled demo mode: roles come from VITE_DEMO_ROLES (dev tooling
      // only — demoFallbackRoles() returns [] in every real auth mode).
      void fetchIamAccessProfile({ rolesFallback: demoFallbackRoles() });
      setAccessProfileBooting(false);
      return;
    }
    if (status !== 'authenticated') {
      clearIamAccessProfile();
      setAccessProfileBooting(false);
      return;
    }
    const stored = getStoredClaims();
    const token = getDevAccessToken() || getStoredAccessToken();
    const decoded = stored ?? (token ? decodeJwtPayload(token) : null);
    void loadAccessProfile(profileRoles(decoded));
  }, [requiresLogin, status, loadAccessProfile]);

  useEffect(() => {
    const capture = capturePendingSmartLaunchFromLocation();
    if (capture.error) {
      setSmartLaunchError(capture.error);
    } else {
      const pendingError = validatePendingSmartLaunch(getPendingSmartLaunch());
      if (pendingError) setSmartLaunchError(pendingError);
    }
  }, []);

  useEffect(() => {
    if (!requiresLogin) {
      setStatus('authenticated');
      return undefined;
    }

    if (passwordMode) {
      let cancelled = false;
      (async () => {
        const authenticated = await ensureAuthenticated();
        if (cancelled) return;
        refreshProfile();
        if (authenticated && hasDevAccessToken()) {
          restoreSmartContextIfAuthenticated();
        }
        setStatus(hasDevAccessToken() ? 'authenticated' : 'anonymous');
        setSessionBooting(false);
      })();
      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;
    (async () => {
      const callback = await completeKeycloakCallbackIfPresent();
      if (cancelled) return;
      if (callback.status === 'error') {
        setLoginError(callback.message);
      }
      const authenticated = await ensureAuthenticated();
      if (cancelled) return;
      refreshProfile();
      if (authenticated && hasDevAccessToken()) {
        restoreSmartContextIfAuthenticated();
      }
      setStatus(hasDevAccessToken() ? 'authenticated' : 'anonymous');
      setOauthBooting(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [requiresLogin, passwordMode, refreshProfile, restoreSmartContextIfAuthenticated]);

  const loginRedirect = useCallback(async (options?: { staySignedIn?: boolean }) => {
    setLoginError(null);
    if (!isKeycloakConfigReady()) {
      setLoginError(
        'Set VITE_KEYCLOAK_ISSUER and VITE_KEYCLOAK_CLIENT_ID for development IAM login.',
      );
      return;
    }
    setStaySignedIn(Boolean(options?.staySignedIn));
    await startKeycloakLogin();
  }, []);

  const loginWithPassword = useCallback(
    async (
      username: string,
      password: string,
      options?: { rememberUsername?: boolean; staySignedIn?: boolean },
    ) => {
      setLoginError(null);
      const result = await loginWithKeycloakPassword(username, password, {
        rememberUsername: options?.rememberUsername,
        staySignedIn: options?.staySignedIn,
        username,
      });
      if (result.status === 'error') {
        setLoginError(result.message);
        throw new Error(result.message);
      }
      refreshProfile();
      // In-app clinician switch (logout → login without a page reload): clear
      // the prior clinician's SMART state now that the new subject is known,
      // before restoring any pending launch context.
      reconcileClinicianScope();
      restoreSmartContextIfAuthenticated();
      setStatus('authenticated');
      const stored = getStoredClaims();
      const token = getDevAccessToken();
      const decoded = stored ?? (token ? decodeJwtPayload(token) : null);
      await loadAccessProfile(profileRoles(decoded));
    },
    [refreshProfile, restoreSmartContextIfAuthenticated, loadAccessProfile],
  );

  const logout = useCallback(() => {
    // SEC-04: wipe the cached clinical transcripts first — the next clinician
    // on this workstation must never inherit them.
    purgeAllThreadStorage();
    // SEC-I1: the Traffic Inspector's persisted feed is the same PHI posture —
    // its events carry tool results (chart content), it is bounded by bytes
    // rather than emptied, and no existing purge covered it, so it survived
    // sign-out on a shared workstation. See contexts/TrafficStreamContext.tsx.
    purgePersistedTrafficEvents();
    // Release cross-tab active-context ownership, then evict the SMART patient
    // session (best-effort) BEFORE revoking tokens, so the bearer is still valid
    // for the close call and the next clinician never inherits the context.
    getPatientContextCoordinator().release();
    void closePatientConnection().finally(() => {
      void logoutSession().finally(() => {
        clearIamAccessProfile();
        setProfile(null);
        setStatus('anonymous');
        setLoginError(null);
      });
    });
  }, []);

  const clearLoginError = useCallback(() => {
    setLoginError(null);
  }, []);

  const configReady = passwordMode ? isPasswordGrantConfigReady() : isKeycloakConfigReady();

  const value = useMemo<AuthContextValue>(
    () => ({
      status: requiresLogin ? status : 'authenticated',
      profile: requiresLogin ? profile : null,
      displayName: requiresLogin ? profileDisplayName(profile) : '',
      roles: requiresLogin ? profileRoles(profile) : [],
      loginRedirect,
      loginWithPassword,
      logout,
      isDevIam: devIamMode,
      isLocalIam: devIamMode,
      isLocalKeycloak: devIamMode,
    }),
    [requiresLogin, devIamMode, status, profile, loginRedirect, loginWithPassword, logout],
  );

  if (!requiresLogin) {
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  }

  if ((oauthBooting && redirectMode) || (sessionBooting && passwordMode)) {
    return <LoadingShell />;
  }

  if (status === 'anonymous') {
    return (
      <AuthContext.Provider value={value}>
        <LoginPage
          mode={authMode}
          onRedirectLogin={loginRedirect}
          onPasswordLogin={loginWithPassword}
          error={loginError ?? smartLaunchError}
          onClearError={clearLoginError}
          configReady={configReady}
          smartLaunchPending={hasPendingSmartLaunch()}
          initialUsername={getRememberedUsername()}
          initialStaySignedIn={getStaySignedIn()}
        />
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
